import { quotaCovers, type NumberQuota } from '@ast-data/shared';

/**
 * What a record-number field offers (Fundzettel-, Befund-, Zeichenblattnummer):
 * the highest number given, and the next.
 */
export interface RecordNumberHint {
    highest: number;
    /** Null where no Kontingent leaves a number above the highest. */
    next: number | null;
}

/** A whole record number ("42"); anything else ('', "12a") is none. */
function wholeNumber(value: string): number | null {
    const trimmed = value.trim();
    return /^\d+$/.test(trimmed) ? Number(trimmed) : null;
}

/**
 * How far above the rest a number may lie and still count, where no
 * Kontingent says which numbers are the Maßnahme's own. Imports carry
 * placeholders far above the real range — the _49 holds Fundzettel 100001 over
 * a run ending at 90812 — and one of them would make every suggestion useless.
 * A real new block is counted from its second number on.
 */
const OUTLIER_DISTANCE = 1000;

/**
 * The highest number the Maßnahme has given — across its Teilmaßnahmen, as the
 * duplicate check counts — and the one after it. The HIGHEST, not the last
 * one entered: a record typed up late must not pull the suggestion back down.
 * An Unterfundzettel ("42_2") shares its number and counts as that number.
 *
 * With Kontingente only the numbers inside them count, and the next is the
 * first allowed number above the highest: the start of the following range
 * once the current one is used up. Without, a number lying more than
 * OUTLIER_DISTANCE above the next lower one is taken for a placeholder.
 *
 * `currentId` is the record being edited, which does not count against itself.
 */
export function recordNumberHint(
    records: readonly { id: string; number: string }[],
    currentId: string,
    quotas: readonly NumberQuota[]
): RecordNumberHint | null {
    const numbers = records
        .filter((record) => record.id !== currentId)
        .map((record) => wholeNumber(record.number))
        .filter((number): number is number => number !== null)
        .filter((number) => quotas.length === 0 || quotaCovers(quotas, number))
        .sort((first, second) => first - second);
    if (quotas.length === 0) {
        while (
            numbers.length > 1 &&
            numbers[numbers.length - 1] - numbers[numbers.length - 2] >
                OUTLIER_DISTANCE
        ) {
            numbers.pop();
        }
    }
    const highest = numbers[numbers.length - 1];
    if (highest === undefined) {
        return null;
    }
    const following = highest + 1;
    if (quotas.length === 0 || quotaCovers(quotas, following)) {
        return { highest, next: following };
    }
    const starts = quotas
        .map((quota) => quota.from)
        .filter((start) => start > highest);
    return { highest, next: starts.length > 0 ? Math.min(...starts) : null };
}

/**
 * The numbers a typed one would skip: from the next free number up to just
 * below it. Null where nothing is skipped — the next itself, a lower number
 * (the duplicate check has its own say), or no number at all.
 */
export function recordNumberGap(
    hint: RecordNumberHint | null,
    typed: string
): { from: number; to: number } | null {
    const number = wholeNumber(typed);
    if (!hint?.next || number === null || number <= hint.next) {
        return null;
    }
    return { from: hint.next, to: number - 1 };
}
